<template>
  <b-form v-unsave-detector class="ml-3 mt-2" @submit.stop.prevent="onSubmit">
    <div class="d-flex justify-content-between">
      <div class="bold-text secondary-color mb-2">{{ $t('bank.loan.General.infos') }}</div>
      <b-button class="mr-2" variant="outline-primary" @click="skip">{{ $t('bank.loan.skip') }}</b-button>
    </div>
    <div class="row">
      <div class="col-md-6">
        <b-form-group
          :invalid-feedback="form.montant.invalidFeedback"
          :label="$t('bank.loan.amount')"
          :state="form.montant.state"
          class="medium-label mb-4"
          label-for="montant-input">
          <b-input-group>
            <b-form-input
              id="montant-input"
              v-model="form.montant.value"
              v-disable-scroll
              :state="form.montant.state"
              min="0"
              required
              step="any"
              type="number"
              @input="calculateInterest">
            </b-form-input>
            <b-input-group-append>
              €
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>

      <div class="col-md-6">
        <b-form-group
          :invalid-feedback="form.rate.invalidFeedback"
          :label="$t('bank.loan.interest.rate')"
          :state="form.rate.state"
          class="medium-label mb-4"
          label-for="rate-input">
          <b-input-group>
            <b-form-input
              id="rate-input"
              v-model="form.rate.value"
              v-disable-scroll
              max="100"
              min="0"
              required
              step="0.01"
              type="number"
              @input="calculateInterest"/>
            <b-input-group-append>
              <div class="pourcentage">
                %
              </div>
              <div v-if="interestAmount>0" class="estimed-value">
                {{ '~ ' + interestAmount.toFixed(2) + '€' }}
              </div>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>
    </div>
    <div class="row">
      <div class="col-md-6">
        <b-form-group
          :invalid-feedback="form.fraisComEngagement.invalidFeedback"
          :label="$t('bank.loan.frais.com.engagement')"
          :state="form.fraisComEngagement.state"
          class="medium-label mb-4"
          label-for="frais-com-input">
          <b-input-group>
            <b-form-input
              id="frais-com-input"
              v-model="form.fraisComEngagement.value"
              v-disable-scroll
              :state="form.fraisComEngagement.state"
              min="0"
              required
              step="any"
              type="number"
              @input="calculateInterest"
            />
            <b-input-group-append>
              %
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>
      <div class="col-md-6">
        <b-form-group
          :invalid-feedback="form.euribor.invalidFeedback"
          :label="$t('bank.loan.frais.com.euribor')"
          :state="form.euribor.state"
          class="medium-label mb-4"
          label-for="euribor-input">
          <b-input-group>
            <b-form-input
              id="euribor-input"
              v-model="form.euribor.value"
              v-disable-scroll
              :state="form.euribor.state"
              min="0"
              required
              step="0.01"
              type="number"
              @input="calculateInterest"
            />
            <b-input-group-append>
              %
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>
      <div class="col-md-6">
        <b-form-group
          :invalid-feedback="form.euribor2An.invalidFeedback"
          :label="$t('bank.loan.frais.com.euribor2An')"
          :state="form.euribor2An.state"
          class="medium-label mb-4"
          label-for="euribor2An-input">
          <b-input-group>
            <b-form-input
              id="euribor2An-input"
              v-model="form.euribor2An.value"
              v-disable-scroll
              :state="form.euribor2An.state"
              min="0"
              required
              step="0.01"
              type="number"
            />
            <b-input-group-append>
              %
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>
      <div class="col-md-6">
        <b-form-group
          :invalid-feedback="form.euribor3An.invalidFeedback"
          :label="$t('bank.loan.frais.com.euribor3An')"
          :state="form.euribor3An.state"
          class="medium-label mb-4"
          label-for="euribor3An-input">
          <b-input-group>
            <b-form-input
              id="euribor3An-input"
              v-model="form.euribor3An.value"
              v-disable-scroll
              :state="form.euribor3An.state"
              min="0"
              required
              step="0.01"
              type="number"
            />
            <b-input-group-append>
              %
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6">
        <b-form-group
          :invalid-feedback="form.startDate.invalidFeedback"
          :label="$t('bank.loan.startDate')"
          :state="form.startDate.state"
          class="medium-label mb-4"
          label-for="startDate-input">
          <datepicker-input
            id="startDate-input"
            v-model="form.startDate.value"
            :state="form.startDate.state"
            disabled
            required/>
        </b-form-group>
      </div>

      <div class="col-md-6">
        <b-form-group
          :invalid-feedback="form.duration.invalidFeedback"
          :label="$t('bank.loan.durations')+' '+$t('bank.loan.inMonth')"
          :state="form.duration.state"
          class="medium-label mb-4"
          label-for="duration-input">
          <b-form-input
            id="duration-input"
            v-model="form.duration.value"
            v-disable-scroll
            min="0"
            required
            step="any"
            type="number"/>
        </b-form-group>
      </div>

      <div class="col-md-6">
        <b-form-group
          :invalid-feedback="form.accordPret.invalidFeedback"
          :label="$t('operationPlanning.operation.accord.pret')"
          :state="form.accordPret.state"
          class="medium-label mb-4"
          label-for="accordPret-input">
          <datepicker-input
            id="accordPret-input"
            v-model="form.accordPret.value"
            :state="form.accordPret.state"
            v-on:input="askConfirm=true;"/>
        </b-form-group>
      </div>
    </div>
    <div class="bold-text secondary-color mb-2 mt-4">{{ $t('bank.loan.frais.infos') }}</div>
    <div class="row">
      <div class="col-md-6">
        <b-form-group
          :invalid-feedback="form.fraisDossier.invalidFeedback"
          :label="$t('bank.loan.frais.dossier')"
          :state="form.fraisDossier.state"
          class="medium-label mb-4"
          label-for="frais-dossier-input">
          <b-input-group>
            <b-form-input
              id="frais-dossier-input"
              v-model="form.fraisDossier.value"
              v-disable-scroll
              :state="form.fraisDossier.state"
              min="0"
              required
              step="any"
              type="number"
            />
            <b-input-group-append>
              €
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>
      <div class="col-md-6">
        <b-form-group
          :invalid-feedback="form.fraisHypotheque.invalidFeedback"
          :label="$t('bank.loan.frais.hypotheque')"
          :state="form.fraisHypotheque.state"
          class="medium-label mb-4"
          label-for="frais-hypotheque-input">
          <b-input-group>
            <b-form-input
              id="frais-hypotheque-input"
              v-model="form.fraisHypotheque.value"
              v-disable-scroll
              :state="form.fraisHypotheque.state"
              min="0"
              required
              step="any"
              type="number"
            />
            <b-input-group-append>
              €
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>
    </div>
    <div class="bold-text secondary-color mb-2 mt-4">{{ $t('bank.loan.apport.infos') }}</div>
    <div class="row">
      <div class="col-md-6">
        <b-form-group
          :invalid-feedback="form.apportTresorerie.invalidFeedback"
          :label="$t('bank.loan.apport.tresorerie')"
          :state="form.apportTresorerie.state"
          class="medium-label mb-4"
          label-for="apport-tresorerie-input">
          <b-input-group>
            <b-form-input
              id="apport-tresorerie-input"
              v-model="form.apportTresorerie.value"
              v-disable-scroll
              :state="form.apportTresorerie.state"
              min="0"
              required
              step="any"
              type="number"
            />
            <b-input-group-append>
              €
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6">
        <div class="bold-text secondary-color mb-2 mt-4">{{ $t('bank.loan.apport.ordre.prelevements') }}</div>
        <draggable :list="prelevements" class="draggable-list" group="prelevements">
          <div v-for="element in prelevements" :key="element.name" class="list-item">
            <div class="d-flex investor mb-1 mx-0">
              <menu-2-icon size="18"></menu-2-icon>
              <span class="ml-2"> {{ element.name }} </span>
            </div>
          </div>
        </draggable>
      </div>
      <div class="col-md-6">
        <div class="bold-text secondary-color mb-2 mt-4">{{ $t('bank.loan.apport.ordre.remboursements') }}</div>
        <draggable :list="remboursements" class="draggable-list" group="remboursements">
          <div v-for="element in remboursements" :key="element.name" class="list-item">
            <div class="d-flex investor mb-1 mx-0">
              <menu-2-icon size="18"></menu-2-icon>
              <span class="ml-2"> {{ element.name }} </span>
            </div>
          </div>
        </draggable>
      </div>
    </div>


    <div class="d-flex justify-content-end">
      <b-button class="mr-2" variant="outline-primary" @click="skip">{{ $t('bank.loan.skip') }}</b-button>
      <b-button v-if="!empruntAuthorized"
                :title="$t('bank.loan.authorized')"
                data-placement="bottom"
                data-toggle="tooltip"
                disabled variant="primary">
        <plus-icon class="mr-1" size="18"/>
        {{ $t('Validate') }}
      </b-button>
      <b-button v-else type="submit" variant="primary">
        {{ $t('Validate') }}
      </b-button>
    </div>

  </b-form>

</template>

<script>
import api from "../../../src/services/api";
import BackArrow from "../../components/b122/BackArrow";
import draggable from 'vuedraggable';
import DatepickerInput from "../../components/b122/editFields/DatepickerInput.vue";

export default {
  name: "OperationCreateEmprunt",
  components: {
    draggable,
    BackArrow,
    DatepickerInput
  },
  props: {
    acteAchat: {
      default: new Date()
    },
    operationId: {
      default: null
    },
    operationName: {
      default: ''
    },
    amount: {
      default: 0
    },
    duration: {
      default: ''
    },
    interest: {
      default: 0
    },
  },

  mounted() {
    this.fetchOperation()
    if (this.amount > 0) {
      this.form.montant.value = this.amount
      this.form.rate.value = this.interest
      this.form.duration.value = this.duration
    }
  },

  data() {
    return {
      remboursements: [
        {'id': 1, 'name': 'Emprunt'},
        {'id': 2, 'name': 'Investisseurs'}
      ],
      prelevements: [
        {'id': 1, 'name': 'Emprunt'},
        {'id': 2, 'name': 'Investisseurs'},
        {'id': 3, 'name': 'Trésorerie'}
      ],
      interestAmount: 0,
      empruntAuthorized: false,
      form: {
        montant:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        rate:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        euribor:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        euribor2An:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        euribor3An:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        duration:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        accordPret:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        startDate:
          {
            state: null,
            invalidFeedback: null,
            value: this.acteAchat
          },
        fraisDossier:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        fraisComEngagement:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        fraisHypotheque:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        apportTresorerie:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
      }
    }
  },

  watch: {
    'form.montant.value': function () {
      if (Number(this.form.montant.value) === 0 || this.form.montant.value === null || this.form.montant.value === '') {
        this.empruntAuthorized = false
      } else {
        this.empruntAuthorized = true
      }
    }
  },

  methods: {
    async onSubmit() {
      let data = new FormData()
      data.append('montant', this.form.montant.value)
      data.append('rate', this.form.rate.value)
      data.append('euribor', this.form.euribor.value)
      data.append('euribor2An', this.form.euribor2An.value)
      data.append('euribor3An', this.form.euribor3An.value)
      data.append('duration', this.form.duration.value)
      data.append('startDate', this.form.startDate.value)
      data.append('fraisDossier', this.form.fraisDossier.value)
      data.append('fraisComEngagement', this.form.fraisComEngagement.value)
      data.append('fraisHypotheque', this.form.fraisHypotheque.value)
      data.append('apportTresorerie', this.form.apportTresorerie.value)
      data.append('operationId', this.operationId)
      data.append('accordPret', this.form.accordPret.value)
      data.append('ordrePrelevements', JSON.stringify(this.prelevements))
      data.append('ordreRemboursements', JSON.stringify(this.remboursements))

      let url = 'bankCredit'

      await api({
        method: 'POST',
        url: url,
        data: data
      })
        .then((response) => {
          this.$router.push({
            name: 'OperationShow',
            params: {
              operationId: this.operationId,
              currentSection: 0
            }
          }, () => {
            this.$nextTick(() => {
              this.successToast(this.operationName)
            })
          })
        })
        .catch((error) => {
          if (error.response.status === 401 || error.response.status === 404) {
            this.$router.push({
              name: 'OperationIndex',
              params: {
                message: error.response.data.message
              }
            })
          }
        })
    },
    successToast(operationName) {
      this.$bvToast.toast(this.$i18n.t('Operation') + ' "' + this.operationName + '" ' + this.$i18n.t('f.added'), {
        autoHideDelay: 4000,
        toaster: 'b-toaster-bottom-right',
        noCloseButton: true,
        variant: "success",
        solid: true,
      })
    },
    calculateInterest() {
      this.interestAmount = ((Number(this.form.montant.value)) * ((Number(this.form.euribor.value) + Number(this.form.rate.value) + Number(this.form.fraisComEngagement.value)) / 100) / 12)
    },

    async fetchOperation() {
      console.log('uwu')
      await api
        .get(`/operation/${this.operationId}`)
        .then(response => {
          this.form.startDate.value = (new Date(response.data.acteAchat).toLocaleDateString()).split("/").reverse().join("-")
        })
    },

    skip() {
      this.$router.push({
        name: 'OperationShow',
        params: {
          operationId: this.operationId,
          currentSection: 0
        }
      }, () => {
        this.$nextTick(() => {
          this.successToast(this.operationName)
        })
      })
    }

  },
}
</script>

<style scoped>


.input-group input {
  border-right: 1px solid #ced4da !important;
}

.estimed-value::before {
  content: "";
  border-left: 1px solid #ced4da;
  display: block;
  height: 35px;
  left: 0;
  position: absolute;
}

.pourcentage, .estimed-value, .input-group-append {
  min-width: 2em;
  display: flex;
  justify-content: center;
  align-items: center;
}

.estimed-value {
  padding-left: 1em;
  position: relative;
  height: 100%;
}

.investor {
  border-radius: 2px;
  border: solid 1px var(--main-grey);
  padding: 8px;
}

.list-item {
  background-color: #FFFFFF;
}
</style>



// WEBPACK FOOTER //
// src/views/operationEmprunt/OperationCreateEmprunt.vue